import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { StatsCard } from '../common/Card';
import { Typography } from '../common/Typography';
import { Modal, ModalContent, ModalCloseButton } from '../common';

const ClubInfoModal = ({ club, isOpen, onClose }) => {
  const [clubDetails, setClubDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && club) {
      fetchClubDetails();
    }
  }, [isOpen, club]);

  const fetchClubDetails = async () => {
    if (!club) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch detailed club information
      const [
        { data: clubData, error: clubError },
        { data: memberCount, error: memberError },
        { data: recentSessions, error: sessionsError },
        { data: clubOwner, error: ownerError }
      ] = await Promise.all([
        // Get full club details
        supabase
          .from('clubs')
          .select('*')
          .eq('id', club.id)
          .single(),
        
        // Get member count
        supabase
          .from('players_club')
          .select('id', { count: 'exact' })
          .eq('club_id', club.id)
          .eq('is_active', true),
        
        // Get sessions from last week
        supabase
          .from('sessions')
          .select('id, created_at')
          .eq('club_id', club.id)
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
        
        // Get club owner/admin
        supabase
          .from('players_club')
          .select(`
            players:player_id (
              first_name,
              last_name
            )
          `)
          .eq('club_id', club.id)
          .eq('club_role', 'admin')
          .eq('is_active', true)
          .limit(1)
          .single()
      ]);

      if (clubError) throw clubError;
      if (memberError) throw memberError;
      if (sessionsError) throw sessionsError;

      setClubDetails({
        ...clubData,
        memberCount: memberCount?.length || 0,
        recentSessionsCount: recentSessions?.length || 0,
        clubOwner: clubOwner?.players || null
      });
    } catch (err) {
      console.error('Error fetching club details:', err);
      setError('Failed to load club details');
    } finally {
      setLoading(false);
    }
  };

  if (!club) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between border-b border-border-medium p-4 sm:p-6">
          <Typography variant="h2">Club Information</Typography>
          <ModalCloseButton onClose={onClose} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <Typography variant="body" className="ml-2">Loading club details...</Typography>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 text-lg mb-2">⚠️</div>
              <Typography variant="error">{error}</Typography>
            </div>
          ) : clubDetails ? (
            <div className="space-y-6">
              {/* Club Header */}
              <div className="text-center">
                <Typography variant="h1" className="mb-2">{clubDetails.name}</Typography>
                <Typography variant="body-large">{clubDetails.location}</Typography>
              </div>

              {/* Description */}
              {clubDetails.description && (
                <div>
                  <Typography variant="h5" className="mb-2">About</Typography>
                  <Typography variant="body" className="leading-relaxed">{clubDetails.description}</Typography>
                </div>
              )}

              {/* Club Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatsCard
                  icon={
                    <svg className="w-6 h-6 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  }
                  value={clubDetails.memberCount}
                  title="Members"
                  iconBg="bg-info-light"
                />
                <StatsCard
                  icon={
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  }
                  value={clubDetails.recentSessionsCount}
                  title="Sessions This Week"
                  iconBg="bg-info-light"
                />
                <StatsCard
                  icon={
                    <svg className="w-6 h-6 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  }
                  value={clubDetails.strength_rating || 'N/A'}
                  title="Club Rating"
                  iconBg="bg-warning-light"
                />
                <StatsCard
                  icon={
                    clubDetails.is_public ? (
                      <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )
                  }
                  value={clubDetails.is_public ? 'Public' : 'Private'}
                  title="Club Type"
                  iconBg={clubDetails.is_public ? 'bg-success-light' : 'bg-surface-subtle'}
                />
              </div>

              {/* Club Owner */}
              {clubDetails.clubOwner && (
                <div>
                  <Typography variant="h5" className="mb-2">Club Owner</Typography>
                  <div className="bg-surface-subtle rounded-lg p-4 border border-border-light">
                    <Typography variant="body" className="font-medium">
                      {clubDetails.clubOwner.first_name} {clubDetails.clubOwner.last_name}
                    </Typography>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div>
                <Typography variant="h5" className="mb-2">Contact Information</Typography>
                <div className="space-y-2">
                  {clubDetails.contact_email && (
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-text-muted mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <Typography variant="body">{clubDetails.contact_email}</Typography>
                    </div>
                  )}
                  {clubDetails.website_url && (
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-text-muted mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <Typography 
                        variant="link" 
                        as="a"
                        href={clubDetails.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {clubDetails.website_url}
                      </Typography>
                    </div>
                  )}
                  {clubDetails.social_media && (
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-text-muted mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <Typography variant="body">{clubDetails.social_media}</Typography>
                    </div>
                  )}
                </div>
              </div>

              {/* Club Age */}
              <div>
                <Typography variant="h5" className="mb-2">Club Details</Typography>
                <div className="bg-surface-subtle rounded-lg p-4 border border-border-light">
                  <Typography variant="body-small">
                    <span className="font-medium">Established:</span> {' '}
                    {new Date(clubDetails.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                  {clubDetails.location_details && (
                    <Typography variant="body-small" className="mt-2">
                      <span className="font-medium">Location Details:</span> {clubDetails.location_details}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-end border-t border-border-medium bg-surface-subtle p-4 sm:p-6">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-secondary text-text-inverse rounded-lg hover:opacity-90 transition-colors"
          >
            Close
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ClubInfoModal;
